import { CameraPhotoType, CameraType } from '@EcamModel/controllers/ICameraHttpController';
import { CameraPhotos, GeoZoneType } from '@EcamModel/model';
import { CameraView } from '@EcamModel/model/CameraView';
import IcEdit from '@assets/details-camera-icons/IcEdit';
import ChangeTypePhotoGeoZone from '@assets/jsx-icon/ChangeTypePhotoGeoZone';
import PopUpWarning from '@components/PopUpWarning';
import useMedia from '@hooks/useMedia';
import usePopUp from '@hooks/usePopUp';
import { Autorenew } from '@mui/icons-material';
import { Box, Button, CircularProgress, Dialog, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { getCenterFrame } from '@pages/cameras/details/utils';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import NoDataCameras from '@pages/cameras/list/components/NoDataCameras';
import color from '@theme/Colors';
import Konva from 'konva';
import { isNil } from 'lodash';
import moment from 'moment';
import React, { ReactNode, useRef, useState } from 'react';
import { Circle, Image, Layer, Line, Stage, Text } from 'react-konva';
import { formatDateByTimeZone, formatUKDateTime } from 'src/helpers';
import { useBackdrop } from 'src/providers/BackdropProvider';
import PopupSetCameraPhoto from './PopupSetCameraPhoto';
import TextBgBackdrop from './TextBgBackdrop';

type Props = {
    isHideAction?: boolean;
    cameraType?: CameraType;
};

export default function WrapHistoryPhoto(props: Props) {
    const backdrop = useBackdrop();

    const { isHideAction = false } = props;

    const { notAvailable, handleRefreshImage, camera } = useCameraDetailContext();

    const openBackdrop = () => {
        const message = <TextBgBackdrop text="Please wait for the camera to take a picture..." />;
        backdrop.setTrue(message);
    };

    return (
        <Stack>
            <Box borderRadius={2} bgcolor={color.white}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={'space-between'}
                    spacing={1}
                    p={2}
                    sx={{
                        borderRadius: 2,
                        height: 50,
                    }}
                >
                    <Stack direction={'row'} spacing={1} sx={{ alignItems: 'center' }}>
                        <Typography color={color.darkPrimary} variant="h5" width={'100%'} mb={1}>
                            Photo
                        </Typography>
                    </Stack>
                    {!isHideAction && (
                        <Stack direction={'row'} spacing={1} alignItems={'center'}>
                            <Tooltip title="Refresh latest photo" placement="top" arrow>
                                <IconButton
                                    sx={{
                                        borderRadius: '3px',
                                        width: '24px',
                                        height: '24px',
                                        color: 'white',
                                        backgroundColor: '#2E7D32',
                                        ':hover': {
                                            backgroundColor: '#256428',
                                        },
                                        '&.MuiIconButton-root.Mui-disabled': {
                                            backgroundColor: '#eee',
                                        },
                                        '&:hover .rotate-icon': {
                                            transform: 'rotate(90deg) scale(1.1)',
                                        },
                                    }}
                                    onClick={() => {
                                        openBackdrop();
                                        handleRefreshImage().finally(() => {
                                            backdrop.setFalse();
                                        });
                                    }}
                                    disabled={notAvailable || backdrop.open}
                                >
                                    <Autorenew
                                        sx={{ fontSize: 17, transition: 'transform 0.3s ease-in-out' }}
                                        className="rotate-icon"
                                    />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    )}
                </Stack>

                <Divider
                    orientation="vertical"
                    sx={{
                        borderBottomWidth: 1,
                        borderColor: color.grey300,
                        mx: 2,
                    }}
                />

                <Stack p={2} pb={2} spacing={1}>
                    <Typography variant="body1" color={'#85858A'}>
                        Last angle deviation check:{' '}
                        {camera?.LastCheckAngleAt ? formatUKDateTime(camera?.LastCheckAngleAt) : 'N/A'}
                    </Typography>
                    <HistoriesPhoto {...props} />
                </Stack>
            </Box>
        </Stack>
    );
}

export interface PreviewPhoto {
    previewOriginal: boolean;
    previewLasted: boolean;
    previewLastedEdited: boolean;
}

function HistoriesPhoto(props: Props) {
    const stageRef = useRef<Konva.Stage>(null);
    const {
        imageLatest,
        imageOriginal,
        imageLatestEdited,
        scaleNotFullLatestCameraSolar,
        scaleFullLatestCameraSolar,
        scaleNotFullOriginalCameraSolar,
        scaleFullOriginalCameraSolar,
        scaleNotFullLatestEditedCameraSolar,
        scaleFullLatestEditedCameraSolar,
        scaledPrevGeoZoneCameraSolar,
        prevGeoZonesCameraSolar,
        blobLatestPhoto,
        blobOriginalPhoto,
        blobLatestEditedPhoto,
        containerRef,
        containerSize,
        captureAtOriginal,
        captureAtLatest,
        captureAtLastEdited,
        timeZone,
        handleSetOriginalPhoto,
    } = useCameraDetailContext();

    const { isMediumMD, isMobileSM } = useMedia();

    const [openPreviewPhoto, setOpenPreviewPhoto] = useState<PreviewPhoto>({
        previewLasted: false,
        previewOriginal: false,
        previewLastedEdited: false,
    });

    const setPartialOpenPreviewPhoto = (p: Partial<PreviewPhoto>) => {
        setOpenPreviewPhoto((prev) => ({ ...prev, ...p }));
    };

    const imageConfigs = [
        {
            blob: blobOriginalPhoto,
            image: imageOriginal,
            heading: 'Original photo',
            previewKey: 'previewOriginal',
            fullScale: scaleFullOriginalCameraSolar,
            scaleNotFull: scaleNotFullOriginalCameraSolar,
            captureAt: captureAtOriginal,
        },
        {
            blob: blobLatestEditedPhoto,
            image: imageLatestEdited,
            heading: 'Latest edited photo',
            previewKey: 'previewLastedEdited',
            fullScale: scaleFullLatestEditedCameraSolar,
            scaleNotFull: scaleNotFullLatestEditedCameraSolar,
            captureAt: captureAtLastEdited,
        },
        {
            blob: blobLatestPhoto,
            image: imageLatest,
            heading: 'Current photo',
            previewKey: 'previewLasted',
            fullScale: scaleFullLatestCameraSolar,
            scaleNotFull: scaleNotFullLatestCameraSolar,
            captureAt: captureAtLatest,
        },
    ];

    const renderImagePreview = ({ blob, image, heading, previewKey, fullScale, scaleNotFull, captureAt }) => {
        if (!blob || !image) {
            return (
                <Stack width={'100%'}>
                    <NoDataCameras
                        sx={{
                            border: '1px solid #DDDDDD',
                            minHeight: containerSize.height,
                        }}
                    />
                </Stack>
            );
        }

        return (
            <ImagePreviewWithGeoZone
                key={heading}
                stageRef={stageRef}
                containerRef={containerRef}
                containerSize={containerSize}
                fullScale={fullScale}
                scaleNotFull={scaleNotFull}
                scaledGeoZones={scaledPrevGeoZoneCameraSolar!}
                prevGeoZones={prevGeoZonesCameraSolar!}
                openPreviewPhoto={openPreviewPhoto}
                image={image!}
                heading={heading}
                previewKey={previewKey}
                cameraType={props.cameraType}
                timeZone={timeZone!}
                captureAt={captureAt}
                onSetPartialOpenPreviewPhoto={setPartialOpenPreviewPhoto}
                onSetOriginalPhoto={handleSetOriginalPhoto}
                onClickPreview={() => setPartialOpenPreviewPhoto({ [previewKey]: true })}
                numberPreviewImage={imageConfigs.length}
            />
        );
    };

    return (
        <Stack direction={isMediumMD ? 'column' : 'row'} justifyContent="space-between" width="100%" spacing={2}>
            {imageConfigs.map((config, index) => renderImagePreview(config))}
        </Stack>
    );
}

export const BaseBoxDecoration = ({
    heading,
    date,
    timeZone,
    previewKey,
    onSetOriginalPhoto,
}: {
    heading?: ReactNode;
    date: Date;
    timeZone?: string;
    previewKey?: keyof PreviewPhoto;
    onSetOriginalPhoto?: (photoType: CameraPhotoType, cameraPhotoId?: number) => Promise<void>;
}) => {
    const popUpWarningChangeOriginalPhoto = usePopUp();
    const popupSetCameraPhoto = usePopUp();

    const [selectedItemsLivePhotos, setSelectedItemsLivePhotos] = useState<CameraPhotos | null>(null);

    const isOriginal = previewKey === 'previewOriginal';

    return (
        <>
            <Stack
                width={'100%'}
                display={previewKey || heading ? 'block' : 'none'}
                sx={{
                    p: '12px',
                    background: 'rgba(0, 0, 0, 0.5)',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    color: '#fff',
                    zIndex: 1,
                }}
            >
                {heading}
                {previewKey === 'previewLasted' ? (
                    <Box position={'absolute'} top={'13%'} right={'2%'}>
                        <Tooltip title="Set original photo" placement="top" arrow>
                            <Button
                                variant="cancelSmall"
                                sx={{
                                    borderRadius: '50%',
                                    p: '4px',
                                    minWidth: '24px',
                                    bgcolor: 'transparent',
                                    border: `2px solid ${'#DDDDDD'}`,
                                    ':hover': {
                                        bgcolor: 'transparent',
                                        transition: '0.3s ease-in-out',
                                    },
                                }}
                                onClick={() => {
                                    popUpWarningChangeOriginalPhoto.setTrue();
                                }}
                            >
                                <ChangeTypePhotoGeoZone />
                            </Button>
                        </Tooltip>
                    </Box>
                ) : (
                    <Box
                        position={'absolute'}
                        top={'30%'}
                        right={'2%'}
                        display={
                            previewKey === 'previewOriginal' || previewKey === 'previewLastedEdited' ? 'block' : 'none'
                        }
                    >
                        <Tooltip title={`Set ${isOriginal ? 'original' : 'latest edited'} photo`} placement="top" arrow>
                            <Box
                                sx={{
                                    color: color.white,
                                    transition: '.2s ease-in-out',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: (theme) => theme.palette.secondary.main,
                                    },
                                }}
                                onClick={() => {
                                    popupSetCameraPhoto.setTrue();
                                }}
                            >
                                <IcEdit />
                            </Box>
                        </Tooltip>
                    </Box>
                )}
            </Stack>
            <Stack
                sx={{
                    py: 1,
                    px: 2,
                    background: 'rgba(0, 0, 0, 0.5)',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    textAlign: 'center',
                    color: '#fff',
                }}
            >
                {formatDateByTimeZone(date, timeZone ?? undefined, true)}
            </Stack>

            <PopUpWarning
                {...popUpWarningChangeOriginalPhoto}
                title="Confirm"
                message="Are you sure you want to set this current photo as the original photo?"
                onConfirm={() => {
                    onSetOriginalPhoto?.(CameraPhotoType.Original).finally(() => {
                        popUpWarningChangeOriginalPhoto.onClose?.();
                    });
                }}
            />

            <PopupSetCameraPhoto
                {...popupSetCameraPhoto}
                selectedItemsLivePhotos={selectedItemsLivePhotos}
                onSelectedItemLivePhoto={(item) => {
                    setSelectedItemsLivePhotos(item);
                }}
                onConfirm={() => {
                    onSetOriginalPhoto?.(
                        isOriginal ? CameraPhotoType.Original : CameraPhotoType.LatestEdit,
                        selectedItemsLivePhotos?.Id!
                    ).finally(() => {
                        setSelectedItemsLivePhotos(null);
                        popupSetCameraPhoto.onClose?.();
                    });
                }}
                photoType={isOriginal ? CameraPhotoType.Original : CameraPhotoType.LatestEdit}
                title={isOriginal ? 'original' : 'latest edited'}
            />
        </>
    );
};

export const PreviewImageWithGeoZone = ({
    open,
    onClose,
    imgSrc,
    scaleX,
    scaleY,
    geoZones,
    cameraType,
    listCameraView,
    widthPreview = 1280,
    heightPreview = 720,
}: {
    open: boolean;
    onClose?(): void;
    imgSrc: HTMLImageElement | undefined;
    scaleX?: number;
    scaleY?: number;
    geoZones?: {
        x: number;
        y: number;
        geoZoneId: number | undefined;
    }[][];
    cameraType?: CameraType;
    listCameraView?: CameraView;
    widthPreview?: number;
    heightPreview?: number;
}) => {
    const stageRef = useRef<Konva.Stage>(null);

    return (
        <Dialog
            open={open}
            onClose={() => {
                onClose?.();
            }}
            PaperProps={{
                sx: {
                    minWidth: widthPreview,
                    minHeight: heightPreview,
                },
            }}
        >
            <div style={{ width: widthPreview, height: heightPreview }}>
                {!imgSrc ? (
                    <Stack direction="row" width="100%" justifyContent="center" alignItems="center" minHeight={720}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <Stage ref={stageRef} width={widthPreview} height={heightPreview}>
                        <Layer>
                            <Image image={imgSrc} scaleX={scaleX} scaleY={scaleY} />
                            {!!geoZones?.length &&
                                geoZones?.map((frame, frameIndex) => {
                                    if (!frame || !frame.length) {
                                        return null;
                                    }

                                    const center = getCenterFrame(frame);
                                    const firstPoint = frame[0];

                                    const matchingGeoZone = listCameraView?.GeoZones?.find(
                                        (zone) => zone.Id === frame[0].geoZoneId
                                    );
                                    const strokeColor =
                                        matchingGeoZone?.GeoZoneType === GeoZoneType.Detail
                                            ? color.accentPink
                                            : color.primary;

                                    return (
                                        <React.Fragment key={`frame-${frameIndex}`}>
                                            <Line
                                                points={frame.flatMap((point) => [point.x, point.y])}
                                                stroke={
                                                    isNil(firstPoint?.geoZoneId)
                                                        ? 'red'
                                                        : cameraType === CameraType.PTZ
                                                          ? strokeColor
                                                          : color.accentPink
                                                }
                                                strokeWidth={3}
                                                fill="rgba(0, 0, 0, 0.12)"
                                                closed={true}
                                            />
                                            {!isNil(firstPoint?.geoZoneId) && (
                                                <>
                                                    <Circle x={center.x} y={center.y} radius={15} fill={color.white} />
                                                    <Text
                                                        x={center.x}
                                                        y={center.y}
                                                        text={String(`${frameIndex + 1}`)}
                                                        fontSize={14}
                                                        fill={color.textPrimary}
                                                        align={'center'}
                                                        verticalAlign={'middle'}
                                                        offsetX={4}
                                                        offsetY={6}
                                                    />
                                                </>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                        </Layer>
                    </Stage>
                )}
            </div>
        </Dialog>
    );
};

interface ImagePreviewWithGeoZoneProps {
    image: HTMLImageElement;
    scaleNotFull: number;
    containerRef: any;
    fullScale: number;
    stageRef: React.RefObject<Konva.Stage>;
    scaledGeoZones: {
        x: number;
        y: number;
        geoZoneId: number | undefined;
    }[][];
    prevGeoZones: {
        x: number;
        y: number;
        geoZoneId: number | undefined;
    }[][];
    containerSize: { width: number; height: number };
    previewKey: keyof PreviewPhoto;
    openPreviewPhoto: PreviewPhoto;
    heading: string;
    timeZone: string;
    captureAt?: Date;
    cameraType?: CameraType;
    onSetPartialOpenPreviewPhoto: (p: Partial<PreviewPhoto>) => void;
    onClickPreview: () => void;
    onSetOriginalPhoto?: (photoType: CameraPhotoType, cameraPhotoId?: number) => Promise<void>;
    numberPreviewImage: number;
}

const ImagePreviewWithGeoZone: React.FC<ImagePreviewWithGeoZoneProps> = ({
    image,
    stageRef,
    scaleNotFull,
    containerRef,
    fullScale,
    scaledGeoZones,
    prevGeoZones,
    containerSize,
    previewKey,
    openPreviewPhoto,
    heading,
    timeZone,
    captureAt,
    cameraType,
    onSetPartialOpenPreviewPhoto,
    onClickPreview,
    onSetOriginalPhoto,
    numberPreviewImage,
}) => {
    const { isMediumMD, isMobileSM } = useMedia();

    return (
        <Stack width={isMediumMD ? '100%' : `calc(100% / ${numberPreviewImage} - 10px)`}>
            <div
                ref={containerRef}
                style={{
                    width: '100%',
                    height: containerSize.height,
                    position: 'relative',
                }}
            >
                <Stage
                    ref={stageRef}
                    width={containerSize.width}
                    height={containerSize.height}
                    style={{ cursor: 'pointer' }}
                >
                    <Layer>
                        <Image image={image} scaleX={scaleNotFull} scaleY={scaleNotFull} onClick={onClickPreview} />
                        {!!scaledGeoZones.length &&
                            scaledGeoZones.map((frame, frameIndex) => {
                                const center = getCenterFrame(frame);
                                return (
                                    <React.Fragment key={`frame-${frameIndex}`}>
                                        <Line
                                            points={frame.flatMap((point) => [point.x, point.y])}
                                            stroke={color.accentPink}
                                        />
                                        <Circle x={center.x} y={center.y} radius={15} fill="#fff" />
                                        <Text
                                            x={center.x}
                                            y={center.y}
                                            text={`${frameIndex + 1}`}
                                            fontSize={14}
                                            fill="black"
                                            align="center"
                                            verticalAlign="middle"
                                            offsetX={4}
                                            offsetY={6}
                                        />
                                    </React.Fragment>
                                );
                            })}
                    </Layer>
                </Stage>
                <BaseBoxDecoration
                    timeZone={timeZone}
                    heading={heading}
                    date={captureAt ?? moment().toDate()}
                    previewKey={previewKey}
                    onSetOriginalPhoto={onSetOriginalPhoto}
                />
                <PreviewImageWithGeoZone
                    open={openPreviewPhoto[previewKey]}
                    onClose={() => onSetPartialOpenPreviewPhoto({ [previewKey]: false })}
                    imgSrc={image}
                    scaleX={fullScale}
                    scaleY={fullScale}
                    geoZones={prevGeoZones}
                    cameraType={cameraType}
                />
            </div>
        </Stack>
    );
};
